import gql from 'graphql-tag'

import {
  observationComments,
  observationContributors,
  observationInfo,
  observationLearningContentAssets,
  observationLearningOutcomes,
  observationLearningRecords,
  observationMedia,
  observationReflections,
  observationRooms,
  observationTaggedChildrenWithInfo,
} from '~/modules/observations'

export const GET_DRAFT_OBSERVATIONS_COUNT = gql`
  query Observations($serviceFkey: String, $first: Int) {
    context(fkey: $serviceFkey) {
      fkey
      observations(first: $first, sortBy: { field: "updated_at", order: "desc" }, status: "draft") {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            updatedAt
          }
        }
      }
    }
  }
`

export const GET_OBSERVATIONS = gql`
  query Observations(
    $bookmarked: Boolean
    $cursor: String
    $status: String
    $serviceFkey: String
    $folderId: Int
  ) {
    context(fkey: $serviceFkey) {
      fkey
      observations(
        first: 20
        after: $cursor
        sortBy: { field: "updated_at", order: "desc" }
        status: $status
        bookmarked: $bookmarked
        folderId: $folderId
      ) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            bookmarked
            externalId
            media {
              id
              coverUrl
              mimeType
              thumbUrl
            }
            commentCount
            comments {
              id
              content
            }
            observationDate
            publishedAt
            updatedAt
            status
            title
            serviceFkey
          }
        }
      }
    }
  }
`

export const GET_OBSERVATION = gql`
  query ObservationDetail($id: Int!, $serviceFkey: String) {
    context(fkey: $serviceFkey) {
      fkey
      observation(id: $id) {
        id
        serviceFkey
        ...ObservationComments
        ...ObservationContributors
        ...ObservationInfo
        ...ObservationLearningOutcomes
        ...ObservationLearningRecords
        ...ObservationMedia
        ...ObservationRooms
        ...ObservationTaggedChildrenWithInfo
        ...ObservationLearningContentAssets
      }
    }
  }
  ${observationComments}
  ${observationContributors}
  ${observationInfo}
  ${observationLearningOutcomes}
  ${observationLearningRecords}
  ${observationMedia}
  ${observationRooms}
  ${observationTaggedChildrenWithInfo}
  ${observationLearningContentAssets}
`

export const GET_OBSERVATION_WITH_REFLECTIONS = gql`
  query ObservationWithReflections($id: Int!, $serviceFkey: String) {
    context(fkey: $serviceFkey) {
      fkey
      observation(id: $id) {
        id
        serviceFkey
        ...ObservationComments
        ...ObservationContributors
        ...ObservationInfo
        ...ObservationLearningOutcomes
        ...ObservationLearningRecords
        ...ObservationMedia
        ...ObservationRooms
        ...ObservationTaggedChildrenWithInfo
        ...ObservationLearningContentAssets
        ...ObservationReflections
      }
    }
  }
  ${observationComments}
  ${observationContributors}
  ${observationInfo}
  ${observationLearningOutcomes}
  ${observationLearningRecords}
  ${observationMedia}
  ${observationRooms}
  ${observationTaggedChildrenWithInfo}
  ${observationLearningContentAssets}
  ${observationReflections}
`

export const GET_OBSERVATION_CANVAS = gql`
  query Observation($id: Int!, $serviceFkey: String) {
    context(fkey: $serviceFkey) {
      fkey
      observation(id: $id) {
        id
        title
        children {
          id
          fullName
          fkey
        }
        learningOutcomes {
          name
          id
        }
        media {
          coverUrl
        }
      }
    }
  }
`

export const GET_OBSERVATION_EDIT = gql`
  query ObservationAndCohorts($id: Int!, $serviceFkey: String) {
    service(fkey: $serviceFkey) {
      fkey
      learningFrameworks {
        id
        fullName
        enableCohorts
        ageGroups {
          id
          name
          minMonths
        }
        ranks {
          id
          name
          value
        }
        categories {
          id
          name
          description
        }
        outcomes {
          id
          name
          primaryId
        }
      }
      observation(id: $id) {
        id
        ...ObservationContributors
        ...ObservationInfo
        ...ObservationLearningOutcomes
        ...ObservationLearningRecords
        ...ObservationMedia
        ...ObservationRooms
        ...ObservationTaggedChildrenWithInfo
        ...ObservationLearningContentAssets

        planningDate
      }
      rooms {
        fkey
        name
      }
    }
  }
  ${observationContributors}
  ${observationInfo}
  ${observationLearningOutcomes}
  ${observationLearningRecords}
  ${observationMedia}
  ${observationRooms}
  ${observationTaggedChildrenWithInfo}
  ${observationLearningContentAssets}
`

export const GET_OBSERVATION_TEXT = gql`
  query ObservationText($id: Int!, $serviceFkey: String) {
    context(fkey: $serviceFkey) {
      fkey
      observation(id: $id) {
        id
        title
        description
        status
      }
    }
  }
`

export const GET_CHILDREN_WITH_INFO = gql`
  query ChildrenSummary($serviceFkey: String!, $roomFkey: String) {
    service(fkey: $serviceFkey) {
      fkey
      children(roomFkey: $roomFkey) {
        fullName
        fkey
        id
        image
        isBookedInToday
        otherDetails {
          consents
        }
      }
    }
  }
`

export const GET_FRAMEWORKS = gql`
  query LearningFrameworks($serviceFkey: String!) {
    service(fkey: $serviceFkey) {
      fkey
      learningFrameworks {
        id
        fullName
        enableCohorts
        ageGroups {
          id
          name
          minMonths
        }
        ranks {
          id
          name
          value
        }
        categories {
          id
          name
          description
        }
        outcomes {
          id
          name
          primaryId
        }
      }
    }
  }
`

export const GET_ROOMS = gql`
  query RoomsSummary($serviceFkey: String!) {
    service(fkey: $serviceFkey) {
      fkey
      rooms {
        fkey
        name
      }
    }
  }
`

export const GET_SERVICE_DETAILS = gql`
  query ServiceDetails($serviceFkey: String) {
    service(fkey: $serviceFkey) {
      fkey
      learningFrameworks {
        ageGroups {
          id
          name
          minMonths
        }
        ranks {
          id
          name
          value
        }
        id
        fullName
        enableCohorts
        shortName
        outcomes {
          id
          name
          primaryId
        }
      }
      rooms {
        fkey
        name
      }
    }
  }
`

export const GET_LEARNING_CONTENT_CATEGORIES = gql`
  query LearningContentCategories($serviceFkey: String!) {
    context(fkey: $serviceFkey) {
      fkey
      learningContentCategories {
        id
        name
      }
    }
  }
`

export const GET_LEARNING_CONTENT_ASSETS_FOR_CATEGORY = gql`
  query LearningContentAssets(
    $serviceFkey: String!
    $categoryId: Int
    $cursor: String
    $frameworkOutcomeIds: [Int]
  ) {
    context(fkey: $serviceFkey) {
      fkey
      learningContentCategory(id: $categoryId) {
        id
        assets(first: 10, after: $cursor, frameworkOutcomeIds: $frameworkOutcomeIds) {
          edges {
            node {
              id
              title
              type
              documentLength
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
`

export const GET_LEARNING_CONTENT_SEARCH_ASSETS = gql`
  query LearningContentAssetSearch($serviceFkey: String!, $searchTerm: String, $cursor: String) {
    context(fkey: $serviceFkey) {
      fkey
      learningContentAssets(search: $searchTerm, first: 100, after: $cursor) {
        edges {
          node {
            id
            title
            type
            documentLength
          }
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
`

export const GET_LEARNING_CONTENT_ASSET = gql`
  query LearningContentAsset($serviceFkey: String!, $id: Int!) {
    context(fkey: $serviceFkey) {
      fkey
      learningContentAsset(id: $id) {
        id
        title
        type
        documentLength
        thumbnailUrl
        documentUrl
        thumbnailMimeType
        documentMimeType
        documentHtml
        categories {
          name
        }
      }
    }
  }
`
